import { zodResolver } from "@hookform/resolvers/zod";
import { WebStreamConfigSchema } from "@polifonia/interlude/models";
import { useForm } from "react-hook-form";

import { interlude, type InterludeTypes } from "@/interlude";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
// `WebStreamConfigSchema` above is only used for `zodResolver` (form-side
// validation on submit). The GET query's response is already strictly typed
// as `WebStreamConfig | null` by zodios — see the pre-flight decision in
// Task 1 — so `parseWebStreamConfigOrDefault` below does not re-validate.

type WebStreamConfig = InterludeTypes["WebStreamConfig"];

const defaultLocaleMenuMessage = {
  title: "",
  subtitle: "",
  teamTabText: "",
  memberTabText: "",
  searchPlaceholder: "",
  searchClear: "",
  categoryAll: "",
  consult: "",
  historyEmpty: "",
  findTeam: "",
};

const defaultLocaleReentryMessage = {
  title: "",
  description: "",
  buttonText: "",
};

export const defaultWebStreamConfig = {
  version: 2,
  typography: {
    baseFontSize: 16,
  },
  menu: {
    heroImageUrl: "",
    bannerScale: "medium",
    colors: {
      primaryColor: "#4586F0",
      secondaryBackgroundColor: "#E7F2FF",
      unreadColor: "#D93721",
    },
    messages: {
      enUs: defaultLocaleMenuMessage,
      th: defaultLocaleMenuMessage,
      ja: defaultLocaleMenuMessage,
      zhTw: defaultLocaleMenuMessage,
    },
  },
  reentry: {
    messages: {
      enUs: defaultLocaleReentryMessage,
      th: defaultLocaleReentryMessage,
      ja: defaultLocaleReentryMessage,
      zhTw: defaultLocaleReentryMessage,
    },
  },
} satisfies WebStreamConfig;

/**
 * Normalize the fetched config into a fully-populated form model.
 *
 * The GET response is validated by zodios against `WebStreamConfigSchema`,
 * which treats `menu.colors` and `typography` as optional (frontend-ahead /
 * added-later fields — see the schema comment). Existing stored configs
 * therefore arrive without them, so we fill their defaults here, at
 * `defaultValues` construction time. Doing it here (not via `setValue`) keeps
 * the dirty baseline correct: the defaults are part of the baseline, so merely
 * opening the page never marks the form dirty.
 *
 * Everything else on `raw` is preserved as-is — including fields this page does
 * not manage (e.g. `reentry.keyword` / `lineOaId`, extra locales) that the
 * schema's `.passthrough()` kept — so they round-trip back untouched on save.
 */
export function parseWebStreamConfigOrDefault(
  raw: WebStreamConfig | null | undefined,
): WebStreamConfig {
  if (!raw) return defaultWebStreamConfig;
  return {
    ...raw,
    // Merged rather than replaced wholesale: `typography` is passthrough, so an
    // object that already carries keys this page does not manage must keep them
    // while only the missing `baseFontSize` is defaulted.
    typography: {
      ...raw.typography,
      // Filled, never clamped: a stored value outside the slider's range is
      // still what the channel renders (Vivace clamps it), so narrowing it here
      // would rewrite the customer's config on a save that never touched this
      // field. The slider clamps for display only -- see SizeForm.
      baseFontSize:
        raw.typography?.baseFontSize ??
        defaultWebStreamConfig.typography.baseFontSize,
    },
    menu: {
      ...raw.menu,
      colors: raw.menu.colors ?? defaultWebStreamConfig.menu.colors,
      bannerScale:
        raw.menu.bannerScale ?? defaultWebStreamConfig.menu.bannerScale,
    },
  };
}

const useWebStreamConfigFormInternal = () => {
  const { channel } = ChannelQueriesContext.useData();
  const webStreamConfigQuery = interlude.channel.useSuspenseGetWebStreamConfig({
    params: {
      channelId: channel.id,
    },
  });
  const webStreamConfig = parseWebStreamConfigOrDefault(
    webStreamConfigQuery.data,
  );

  return useForm({
    mode: "onSubmit",
    resolver: zodResolver(WebStreamConfigSchema),
    defaultValues: webStreamConfig,
  });
};

const useWebStreamConfigForm = Object.assign(useWebStreamConfigFormInternal, {
  defaultWebStreamConfig,
});

export { useWebStreamConfigForm };
